import { IAgentConfigDeprecatedService } from '@agent/core/agent/agent-config-deprecated.service';
import { SearchDto } from '@agent/core/basket/mail/mail-basket.model';
import { SortOrder } from '@agent/core/basket/models/basket';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { AppExternalService } from '@commons/core/ui/app-external.service';
import { IHistoryMailService } from '@commons/history/history-mail/hitsory-mail.service';
import { IMailComment } from '@commons/history/history-mail/mail-comment/models/mail-comment';
import { IEpticaExctractAttachment, IEpticaExtractAttachmentFrom } from '@commons/history/history-mail/models/eptica-extract';
import { ChannelDeprecatedImpl as ChannelUtil } from '@commons/models/channel';
import { base32Decode } from '@commons/util/helper/base32';
import { RxUtil } from '@commons/util/utilities/rx';
import { IHttpService, ILogService, IPromise, IQService, isString, ITimeoutService, toJson } from 'angular';
import { i18n } from 'i18next';
import {
  BehaviorSubject,
  catchError,
  delay,
  EMPTY,
  filter,
  first,
  forkJoin,
  from,
  map,
  Observable,
  of,
  retryWhen,
  switchMap,
  take,
  tap,
  throwIfEmpty,
} from 'rxjs';

import {
  AskForHelpEvent,
  AskForHelpStatus,
  BookingFacade,
  CSR_FACADE_PREFIX,
  EAFilter,
  EASortOrder,
  Entity,
  EpticaExposedCustomer,
  EpticaExposedDraft,
  EpticaExposedRequest,
  EpticaFields,
  EpticaInfo,
  EpticaInfoFacade,
  EpticaRequestEventType,
  EpticaRequestState,
  EpticaRequestType as EpticaType,
  EpticaSearchDto,
  EpticaService,
  EpticaTreeAggregationDto,
  EpticaUser,
  EpticaUserFacade,
  isArray,
  isNotNullOrUndefined,
  LoaderDeprecatedService,
  MAIL_FACADE_PREFIX,
  MailForwardParam,
  MailService,
  Memo,
  MomentService,
  notNullOrUndefined,
  Option,
  Profile,
  ProfileFacade,
  ProfileService,
  RequestId,
  Skill,
  ToasterFacade,
  TranslateService,
  truncate,
} from '@contact/upgrade';

import { EpticaHelper } from './eptica.helper';
import { EpticaData } from './models/eptica-data';
import { buildAskForHelpStatusQuery, buildMatchingRequestIdsQuery, buildSearchLastSentMessageFrom } from './models/eptica-request-queries';
import { EpticaRequest } from './models/eptica.request.incoming';
import {
  CustomerIdType,
  EPTICA_OUTGOING_FRAME_PEFIX,
  EPTICA_REQUEST_FRAME_PEFIX,
  EpticaRequestType,
  IEpticaOutgoingFrame,
  IEpticaRequestFrame,
  MailRequest,
  MailRequestResult,
  REQUEST_CREATE_PAGE_NAME,
  REQUEST_PAGE_NAME,
} from './models/eptica.request.interfaces';
import { EpticaOutgoingPage } from './models/eptica.request.outgoing';
import { ILoadInParam, ILoadOutParam } from './models/load-param';

const MAX_TIMEOUT_FOR_LOADER = 120000;
const AREA_MAIN_ACTION_ZONE = 'Area-MainActions';
const EVENT_TYPES: EpticaRequestEventType[] = [
  EpticaRequestEventType.REQUEST_OPENING,
  EpticaRequestEventType.SEND_ASK_FOR_HELP,
  EpticaRequestEventType.BOUNCE_BY_AGENT,
  EpticaRequestEventType.REQUEST_REVIEW_REQUESTED,
  EpticaRequestEventType.REQUEST_AUTO_FORWARDED,
  EpticaRequestEventType.REQUEST_FORWARDED_TO_GROUP,
  EpticaRequestEventType.REQUEST_FORWARDED_BY_AGENT_TO_EXTERNAL_SPECIALIST,
  EpticaRequestEventType.REQUEST_FORWARDED_WITH_ATTACHMENTS,
  EpticaRequestEventType.REQUEST_FORWARDED,
  EpticaRequestEventType.OUTGOING_EMAIL_SENT,
  EpticaRequestEventType.CLIENT_MESSAGE,
  EpticaRequestEventType.REQUEST_FORWARDED_BY_AGENT_TO_GROUP,
  EpticaRequestEventType.RESPONSE,
];

export interface IEpticaDeprecatedService {
  info: EpticaInfo | undefined;
  user: EpticaUser | undefined;
  getRequestDrafts(): Observable<MailRequestResult>;
  getBaseUrl(): IPromise<string>;
  getBaseUrlStr(): string;
  getEpticaLocal(): string;
  getMailAccountId(): string;
  /**
   * Return the eptica csr currently logged in
   */
  getCurrentEpticaCsr(): Option<string>;
  loadRequest$(param: ILoadInParam): Observable<IEpticaRequestFrame>;
  /**
   * Load a request
   * @param {param} ILoadInParam
   */
  loadRequest(param: ILoadInParam): IPromise<IEpticaRequestFrame>;
  /**
   * Load a request, test the predicate when request is loaded. If the predicate is false, retries to load the request.
   * @param {param} ILoadInParam
   * @param {number} nbRetryMax
   * @param {number} delayBetweeRetries in millis
   */
  loadRequestUntil(param: ILoadInParam, nbRetryMax?: number, delayBetweeRetries?: number): IPromise<IEpticaRequestFrame>;
  loadOutgoingRequest$(param: ILoadOutParam): Observable<IEpticaOutgoingFrame>;
  loadOutgoingRequest(param: ILoadOutParam): IPromise<IEpticaOutgoingFrame>;
  /**
   * Retrieve assigned and askForHelp requests
   * @param {string} sortedField
   * @param {string} sortOrder
   * @param {boolean} type
   * @param {boolean} keepOnlyAskForHelp
   * @param {boolean} keepOnlyAnsweredAskForHelp
   * @param {number} [offset=0]
   * @param {number} [pageSize=10]
   * @param {EAFilter[]} [filter] see buildFilter.
   * @returns {Observable<MailRequestResult>}
   */
  getRequests(
    sortedField: string,
    sortOrder: EASortOrder,
    type: EpticaRequestType,
    offset?: number,
    pageSize?: number,
    filter?: EAFilter[]
  ): Observable<MailRequestResult>;

  /**
   * Retrieve requests for specified csr
   * @param {string} csrAccountUUId
   * @param {string} csrAccountGroupHierarchy
   * @param {string} sortedField
   * @param {string} sortOrder
   * @param {boolean} type
   * @param {boolean} keepOnlyAskForHelp
   * @param {boolean} keepOnlyAnsweredAskForHelp
   * @param {number} [offset=0]
   * @param {number} [pageSize=10]
   * @param {Filter[]} [filter] see buildFilter.
   * @returns {Observable<MailRequestResult>}
   */
  getRequestsOfCsrs(
    searchDtos: SearchDto[],
    sortField: string,
    sortOrder: SortOrder,
    type: EpticaRequestType,
    offset?: number,
    pageSize?: number,
    filters?: EAFilter[]
  ): Observable<MailRequestResult>;
  getSearchRequestCount(requestsType: string[]): Observable<{ [key: string]: number }>;
  getRequestVisibility(requestIds: number[]): IPromise<{ [id: string]: boolean }>;
  /**
   * Get the eptica in process requests sent by the specified customerEmail
   * @param {string[]} requestedFields
   * @param {string} sortedField
   * @param {string} sortOrder
   * @param {string[]} epticaRequestIds
   * @param {number} [offset=0]
   * @param {number} [pageSize=10]
   * @returns {Observable<any>}
   */
  getMatchingRequestsByIds(
    epticaRequestIds: string[],
    sortedField: string,
    sortOrder: EASortOrder,
    offset?: number,
    pageSize?: number
  ): Observable<EpticaExposedRequest[]>;
  /**
   * Load the url in the given frame
   */
  loadUrl(frame: HTMLIFrameElement, url: string, expectedPageName: string): Observable<EpticaData>;
  /**
   * Removes the external application associated with the request and the request from the array of displayed requests
   */
  removeIncomingRequest(requestId: number): void;
  /**
   * Removes the external application associated with the request and the request from the array of displayed requests
   */
  removeOutgoingRequest(responseId: string): void;
  /**
   * Returns the url to find a customer
   */
  getCustomerUrl(iasId: string): string;
  /**
   * Close the current outgoing mail.
   */
  closeOutgoingForm(): IPromise<void>;
  /**
   * Hide the frames atching the specified keys
   */
  hideFrames(keys: string[]): void;
  /**
   * Remove the frames atching the specified keys
   */
  removeFrames(keys: string[]): void;
  /**
   * Retrieve the Eptica iasId of a CSR
   * @param {number} csrEsId  The Eptica Server ID of the agent
   */
  getCsrIasId(csrEsId: number): IPromise<string>;
  getCsrIasIdObs(csrEsId: number): Observable<string>;
  /**
   * Retrieve the Eptica Server Id of a CSR
   * @param {number} uuid  The uuid of the agent
   */
  getCsrEsId(uuid: string): IPromise<number>;
  getCsrEsIdObs(uuid: string): Observable<number>;
  /**
   * Search the id of the last outgoing request created by this agent
   * @param {number} accountId    Id of the eptica account of the current csr
   * @return                      the promise of the request id
   */
  searchLastMailRequestSend(accountId: number): IPromise<number>;
  /**
   * Update in eptica the value of a field
   * @param {number} epticaRequestId  The eptica id (ES) of the request
   * @param {Object} fields           Object (associative map) with the name of the fields and their value
   * @return                          A void promise
   */
  updateRequestFields(requestId: RequestId, fields: {}): IPromise<void>;

  /**
   * Respond to an ask for help
   */
  askForHelpAnswer(requestId: RequestId): IPromise<void>;
  /**
   * Link request
   */
  linkRequest(requestId: RequestId, contactConnectionId: string): Observable<RequestId>;
  safeLinkRequest(requestId: RequestId, contactConnectionId: string): Observable<RequestId>;
  /**
   * Defer request
   */
  deferRequest(requestId: RequestId, memo?: Memo, bookmark?: boolean): Observable<RequestId>;
  safeDeferRequest(requestId: RequestId, memo?: Memo, bookmark?: boolean): Observable<RequestId>;
  /**
   * Close request
   */
  closeRequest(requestId: RequestId, comment?: string): Observable<RequestId>;
  safeCloseRequest(requestId: RequestId, comment?: string): Observable<RequestId>;
  /**
   * Ask approval request
   */
  askApprovalRequest(requestId: RequestId, comment?: string): Observable<RequestId>;
  safeAskApprovalRequest(requestId: RequestId, comment?: string): Observable<RequestId>;
  /**
   * Accept approval request
   */
  acceptApprovalRequest(requestId: RequestId, comment?: string): Observable<RequestId>;
  safeAcceptApprovalRequest(requestId: RequestId, comment?: string): Observable<RequestId>;
  /**
   * Reject approval request
   */
  rejectApprovalRequest(requestId: RequestId, comment?: string): Observable<RequestId>;
  safeRejectApprovalRequest(requestId: RequestId, comment?: string): Observable<RequestId>;
  /**
   * Request assistance
   */
  assistRequest(requestId: RequestId): IPromise<void>;
  /**
   * Alias for giveUpRequest
   * @deprecated use giveup
   */
  bounceRequest(requestId: RequestId, comment: IMailComment, responseId?: string): IPromise<RequestId>;
  safeBounceRequest(requestId: RequestId, comment: IMailComment, responseId?: string): IPromise<RequestId>;
  /**
   * Forward a request to queue (bounce a request). The connected account must be
   * the responsible of request or a supervisor of responsible of request, unless it is given as asAccountUuid.
   */
  giveUpRequest(requestId: RequestId, comment: IMailComment, responseId?: string, asAccountUuid?: string): Observable<RequestId>;
  safeGiveUpRequest(requestId: RequestId, comment: IMailComment, responseId?: string): Observable<RequestId>;
  /**
   * Transfer to skill
   */
  transferRequestToSkill(
    requestId: RequestId,
    agent: string,
    targetSkills: Skill[],
    sourceSkills: Skill[],
    comment: string
  ): IPromise<RequestId>;
  /**
   * Transfer to team
   */
  transferRequestToTeam(
    requestId: RequestId,
    agent: string,
    targetTeam: Entity,
    comment: string,
    sourceSkills: Skill[],
    targetSkills: Skill[]
  ): IPromise<RequestId>;
  /**
   * Transfer to agent
   */
  transferRequestToAgent(
    requestId: RequestId,
    target: any,
    comment: string,
    iasId: string,
    request?: IEpticaRequestFrame
  ): Observable<RequestId>;
  safeTransferRequestToAgent(
    requestId: RequestId,
    target: any,
    comment: string,
    iasId: string,
    request?: IEpticaRequestFrame
  ): Observable<RequestId>;
  /**
   * Transfer to mail
   */
  transferRequestToEmail(requestId: RequestId, email: string, comment: string, request?: IEpticaRequestFrame): Observable<RequestId>;
  safeTransferRequestToEmail(requestId: RequestId, email: string, comment: string, request?: IEpticaRequestFrame): Observable<RequestId>;
  /**
   * Forward to skill
   */
  forwardRequestToSkill(
    requestId: RequestId,
    data: {
      source: (Skill | string)[];
      target: (Skill | string)[];
      comment: string;
      attachments?: File[];
    }
  ): Observable<RequestId>;
  safeForwardRequestToSkill(
    requestId: RequestId,
    data: {
      source: (Skill | string)[];
      target: (Skill | string)[];
      comment: string;
      attachments?: File[];
    }
  ): Observable<RequestId>;
  /**
   * Forward to team
   */
  forwardRequestToTeam(
    requestId: RequestId,
    data: {
      target: Entity | string;
      sourceSkills: (Skill | string)[];
      targetSkills: (Skill | string)[];
      comment: string;
      attachments?: File[];
    }
  ): Observable<RequestId>;
  safeForwardRequestToTeam(
    requestId: RequestId,
    data: {
      target: Entity | string;
      sourceSkills: (Skill | string)[];
      targetSkills: (Skill | string)[];
      comment: string;
      attachments?: File[];
    }
  ): Observable<RequestId>;
  /**
   * Forward to agent
   */
  forwardRequestToAgent(
    requestId: RequestId,
    data: {
      target: string;
      comment: string;
      attachments?: File[];
    }
  ): Observable<RequestId>;
  safeForwardRequestToAgent(
    requestId: RequestId,
    data: {
      target: string;
      comment: string;
      attachments?: File[];
    }
  ): Observable<RequestId>;
  /**
   * Forward to mail
   */
  forwardRequestToMail(
    requestId: RequestId,
    data: {
      target: string;
      comment: string;
      attachments?: File[];
      includedMessages?: string[];
    }
  ): Observable<RequestId>;
  safeForwardRequestToMail(
    requestId: RequestId,
    data: {
      target: string;
      comment: string;
    }
  ): Observable<RequestId>;
  /**
   * Assign mail
   */
  assignRequest(requestId: RequestId): Observable<RequestId>;
  safeAssignRequest(requestId: RequestId): Observable<RequestId>;
  /**
   * Check processing treatment request id
   * @param {number} epticaRequestId  The eptica id (ES) of the request
   * @return                          A observable
   */
  checkProcessing$<T>(epticaRequestId: RequestId, action?: Observable<T>, busy?: () => void): Observable<T>;
  /**
   * Check processing treatment request id
   * @param {number} epticaRequestId  The eptica id (ES) of the request
   * @return                          A promise
   */
  checkProcessing<T>(epticaRequestId: RequestId, action?: () => IPromise<T>, busy?: () => void): IPromise<T>;

  /**
   * Returns the assignation urls
   */
  getAssignUrl(requestId: number): string;
  /**
   * used to save attached file for given request id
   * @param requestId the request id to use
   * @param file the attached file
   */
  addAttachment(requestId: string, file: IEpticaExctractAttachment): void;

  /**
   * used to remove saved attached file for given request id
   * @param requestId the request id to use
   * @param fileToRemove the attached file
   */
  removeAttachment(requestId: string, fileToRemove: IEpticaExctractAttachment): void;

  /**
   * usde to retrieve all attachments for givenr equest id and from type
   * @param {string} requestId the request id to use
   * @param {IEpticaExtractAttachmentFrom} from the type to use
   * @returns {IEpticaExctractAttachment[]} the attachments matching
   */
  getAttachmentFrom(requestId: string, from: IEpticaExtractAttachmentFrom): IEpticaExctractAttachment[];

  /**
   * Create the contacts for the CC and BCC fields
   * (the contacts will be create only in there is no existing contact with that adress)
   *
   * @param adresses The emails adresses
   */
  createContactsFromEmails(adresses: Array<string>): Observable<void>;

  /**
   * Used to add PJ events for the loader
   */
  addPJEventsForLoader();

  /**
   * Used to remove PJ events for the loader
   */
  removePJEventsForLoader();

  /**
   * Used to test if the mail has opened ask for help request
   * @param requestId
   */
  isRequestWithOpenedAskForHelp(requestId: string): Observable<boolean>;
}

export type EpticaExposedRequestForDraft = Omit<EpticaExposedRequest, 'creationDate'> & {
  readonly creationDate: number | undefined;
  readonly draftUuid?: string;
};

export class EpticaDeprecatedService implements IEpticaDeprecatedService {
  static $inject: Array<string> = [
    '$log',
    '$q',
    '$http',
    '$timeout',
    'httpDeprecated',
    'externalApplications',
    'agentConfigDeprecatedService',
    'toastDeprecatedService',
    'sessionDeprecatedService',
    '$i18next',
    'contactDeprecatedService',
    'historyMailService',
    'epticaService',
    'mailService',
    'contactFacade',
    'profileService',
    'toasterFacade',
    'translateService',
    'epticaInfoFacade',
    'epticaUserFacade',
    'loaderDeprecatedService',
    'profileFacade',
    'momentService',
  ];

  private incomingRequests$: { [id: string]: IEpticaRequestFrame } = {};
  private outgoingRequests$: { [id: string]: IEpticaOutgoingFrame } = {};
  private attachmentsRequest$: { [id: string]: IEpticaExctractAttachment[] } = {};

  private profile: Profile;

  get info() {
    return this._info$.value;
  }
  private readonly _info$ = new BehaviorSubject<EpticaInfo | undefined>(undefined);
  readonly info$ = this._info$.asObservable();

  get user() {
    return this._user$.value;
  }
  private readonly _user$ = new BehaviorSubject<EpticaUser | undefined>(undefined);
  readonly user$ = this._user$.asObservable();

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly $http: IHttpService,
    private readonly $timeout: ITimeoutService,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly extApp: AppExternalService,
    private readonly agentConfigDeprecatedService: IAgentConfigDeprecatedService,
    private readonly toastDeprecatedService: ToastDeprecatedService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    private readonly $i18next: i18n,
    private readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly historyMailService: IHistoryMailService,
    private readonly epticaService: EpticaService,
    private readonly mailService: MailService,
    private readonly contactFacade: BookingFacade,
    private readonly profileService: ProfileService,
    private readonly toasterFacade: ToasterFacade,
    private readonly translateService: TranslateService,
    private readonly epticaInfoFacade: EpticaInfoFacade,
    private readonly epticaUserFacade: EpticaUserFacade,
    private readonly loaderService: LoaderDeprecatedService,
    private readonly profileFacade: ProfileFacade,
    private readonly timeUtil: MomentService
  ) {
    // display mode
    this.epticaInfoFacade.info$.subscribe(info => this._info$.next(info));
    this.epticaUserFacade.user$.subscribe(user => this._user$.next(user));
    this.profileFacade.profile$.subscribe(profile => (this.profile = profile));
  }

  private getEpticaInfo(): IPromise<EpticaInfo> {
    return RxUtil.toPromise(this.epticaService.info$, this.$q);
  }

  getCurrentEpticaCsr(): Option<string> {
    return Option.of(this.info).map(({ accountId }) => accountId);
  }

  getBaseUrl(): IPromise<string> {
    return this.getEpticaInfo().then(({ iasUrl }) => iasUrl);
  }

  getBaseUrlStr(): string {
    return Option.of(this.info)
      .map(({ iasUrl }) => iasUrl)
      .getOrElse('');
  }

  getEpticaLocal(): string {
    return Option.of(this.info)
      .map(({ locale }) => locale)
      .getOrElse('');
  }

  getMailAccountId(): string {
    return Option.of(this.info)
      .map(({ accountId }) => accountId)
      .getOrElse('');
  }

  getIndexUrl(requestId: number): string {
    return Option.of(this.info)
      .map(({ iasUrl }) => iasUrl)
      .map(baseUrl => `${baseUrl}/serviceRequest/index.action?request.esRequestId=${requestId}`)
      .getOrElse('');
  }

  getAssignUrl(requestId: number): string {
    return Option.of(this.info)
      .map(({ iasUrl }) => iasUrl)
      .map(baseUrl => `${baseUrl}/serviceRequest/assign.action?request.esRequestId=${requestId}`)
      .getOrElse('');
  }

  getSearchUrl(namedQuery: string): string {
    return Option.of(this.info)
      .map(({ iasUrl }) => iasUrl)
      .map(
        baseUrl =>
          `${baseUrl}/serviceRequest/search.action?namedQuery=${namedQuery}&sortColumnKey=lastCustomerEventDate&sortOrder=descending`
      )
      .getOrElse('');
  }

  getOutgoingUrl(idType: CustomerIdType, customerId: string, draftId: string | undefined): string {
    return Option.of(this.info)
      .map(({ iasUrl }) => iasUrl)
      .map(baseUrl => {
        if (!!draftId) {
          return `${baseUrl}/serviceRequest/createIndex.action?savedDraftRequest.draftRequestId=${draftId}`;
        }
        if (idType === 'es') {
          return `${baseUrl}/serviceRequest/createIndex.action?customer.esCustomerId=${customerId}`;
        }
        if (idType === 'ias') {
          return `${baseUrl}/serviceRequest/createIndex.action?customer.customerId=${customerId}`;
        }
        return '';
      })
      .getOrElse('');
  }

  getCustomerUrl(iasId: string): string {
    return Option.of(this.info)
      .map(({ iasUrl }) => iasUrl)
      .map(baseUrl => `${baseUrl}/api/customer/iasId/${iasId}`)
      .getOrElse('');
  }

  loadUrl(frame: HTMLIFrameElement, url: string, expectedPageName: string, showErrorToast = true): Observable<EpticaData> {
    Option.of(url)
      .filter(s => s !== '')
      .forEach(s => (frame.src = s));
    return EpticaHelper.waitForDataObs(frame).pipe(
      tap(data => this.$log.debug(`[load] Loaded url[${url}] expected page name[${expectedPageName}] and page name[${data.pageName}]`)),
      filter(data => data.pageName === expectedPageName),
      first(),
      tap(null, () => {
        this.$log.error(`Failed to load url[${url}] with expected page name[${expectedPageName}]`);
        if (showErrorToast) {
          this.toastDeprecatedService.alert('agent:toast.mail_page_failed');
        }
      })
    );
  }

  /**
   * Hide the frames atching the specified keys
   * key must be : requestId + responseId
   */
  hideFrames(keys: string[]): void {
    keys.forEach((key: string) => {
      Option.of(this.incomingRequests$[key]).forEach(request => request.hide());
      Option.of(this.outgoingRequests$[key]).forEach(request => request.hide());
    });
  }

  /**
   * Hide the frames matching the specified keys
   * key must be : requestId + responseId + specificKeyPart
   */
  removeFrames(keys: string[]): void {
    keys.forEach((key: string) => {
      Option.of(this.incomingRequests$[key]).forEach(request => {
        if (request.isLocked()) {
          return;
        }
        this.extApp.removeApplication(request.getFrameId());
        delete this.incomingRequests$[key];
        this.$log.info(`[eptica] Removed request[${request.getId()}]`);
      });
      Option.of(this.outgoingRequests$[key]).forEach(request => {
        if (request.isLocked()) {
          return;
        }
        this.extApp.removeApplication(request.getFrameId());
        delete this.outgoingRequests$[key];
        this.$log.info(`[eptica] Removed request[${request.getId()}]`);
      });
      Option.of(this.attachmentsRequest$[key]).forEach(() => {
        delete this.attachmentsRequest$[key];
        this.$log.info(`[eptica] Removed attachements[${key}]`);
      });
    });
  }

  loadRequest$(param: ILoadInParam): Observable<IEpticaRequestFrame> {
    const requestId = param.requestId;
    const responseId = param.responseId || '';
    const showErrorToast = Option.of(param.showErrorToast).getOrElse(true);
    const lazy = Option.of(param.lazy).getOrElse(false);
    const forceReload = Option.of(param.forceReload).getOrElse(false);
    const frameId = this.buildFrameId(requestId);
    const url = this.getIndexUrl(requestId);

    if (forceReload) {
      this.getOrElseEpticaRequest(requestId).finish();
    }

    const waitForLoad = frame =>
      this.loadUrl(frame, url, REQUEST_PAGE_NAME, showErrorToast).pipe(
        // extract events eptica
        tap(() =>
          this.contactDeprecatedService.getSelectedContact().then(contact => {
            if (contact.responseId === responseId && contact.epticaRequestId === requestId) {
              this.getOrElseEpticaRequest(requestId)
                .retrieveLastTransferEvent()
                .pipe(isNotNullOrUndefined())
                .subscribe(
                  // keep most recent and update contact information with given elements
                  evt => this.contactDeprecatedService.updateTransferredMailState(responseId, evt),
                  () => this.$log.info(`Load request : can't get comment mail with id [${frameId}]`)
                );
            }
          })
        )
      );

    // get shared observable request eptica or create
    return this.extApp.createApp(frameId, '', lazy, waitForLoad).pipe(map(() => this.getOrElseEpticaRequest(requestId)));
  }

  loadRequest(param: ILoadInParam): IPromise<IEpticaRequestFrame> {
    return RxUtil.toPromise(this.loadRequest$(param), this.$q);
  }

  loadRequestUntil(param: ILoadInParam, nbRetryMax = 3, delayBetweeRetries = 1000): IPromise<IEpticaRequestFrame> {
    const requestId = param.requestId;
    if (nbRetryMax <= 0) {
      this.$log.error(`[eptica] Failed to load request with id[${requestId}] number try out`);
      return this.$q.reject();
    }
    param.showErrorToast = nbRetryMax - 1 <= 0 && param.showErrorToast;
    return this.loadRequest(param).catch(() => {
      this.$log.warn(`[eptica] Try to load request with id[${requestId}] and remain try[${nbRetryMax - 1}]`);
      return this.$timeout(delayBetweeRetries).then(() => this.loadRequestUntil(param, nbRetryMax - 1, delayBetweeRetries));
    });
  }

  loadOutgoingRequest$(param: ILoadOutParam): Observable<IEpticaOutgoingFrame> {
    const idType = param.idType;
    const customerId = param.customerId;
    const responseId = param.responseId || '';
    const lazy = Option.of(param.lazy).getOrElse(false);
    const frameId = this.buildFrameIdForOutgoing(responseId, customerId);
    const draftId = param.draftId;
    const url = this.getOutgoingUrl(idType, customerId, draftId);

    const waitForLoad = frame => this.loadUrl(frame, '', REQUEST_CREATE_PAGE_NAME);
    // get shared observable request eptica or create
    return this.extApp
      .createApp(frameId, url, lazy, waitForLoad)
      .pipe(map(() => this.getOrElseEpticaOutgoingRequest(responseId, idType, customerId)));
  }

  loadOutgoingRequest(param: ILoadOutParam): IPromise<IEpticaOutgoingFrame> {
    return RxUtil.toPromise(this.loadOutgoingRequest$(param), this.$q);
  }

  removeIncomingRequest(requestId: number): void {
    this.getIncomingRequest(requestId).forEach(request => {
      if (request.isLocked()) {
        return;
      }
      request.preRemoveRequest();
      this.extApp.removeApplication(request.getFrameId());
      delete this.incomingRequests$[requestId];
      this.$log.info(`[eptica] Removed request[${request.getId()}]`);
    });
    Option.of(this.attachmentsRequest$[requestId]).forEach(() => {
      delete this.attachmentsRequest$[requestId];
      this.$log.info(`[eptica] Removed attachements[${requestId}]`);
    });
  }

  removeOutgoingRequest(responseId: string): void {
    this.getOutgoingRequest(responseId).forEach(request => {
      if (request.isLocked()) {
        return;
      }
      request.preRemoveRequest();
      this.extApp.removeApplication(request.getFrameId());
      delete this.outgoingRequests$[responseId];
      this.$log.info(`[eptica] Removed request[${request.getId()}]`);
    });
    Option.of(this.attachmentsRequest$[responseId]).forEach(() => {
      delete this.attachmentsRequest$[responseId];
      this.$log.info(`[eptica] Removed attachements[${responseId}]`);
    });
  }

  addAttachment(requestid: string, file: IEpticaExctractAttachment): void {
    if (!this.attachmentsRequest$[requestid]) {
      this.attachmentsRequest$[requestid] = [];
    }
    if (isArray(this.attachmentsRequest$[requestid])) {
      this.attachmentsRequest$[requestid].push(file);
    }
  }

  removeAttachment(requestId: string, fileToRemove: IEpticaExctractAttachment): void {
    if (!!this.attachmentsRequest$[requestId] && isArray(this.attachmentsRequest$[requestId])) {
      const findFiles = this.attachmentsRequest$[requestId].filter(a => a.title === fileToRemove.title);
      findFiles.forEach(findFile => {
        const index = this.attachmentsRequest$[requestId].indexOf(findFile, 0);
        if (index > -1) {
          this.attachmentsRequest$[requestId].splice(index, 1);
        }
      });
    }
  }

  getAttachmentFrom(requestId: string, fromType: IEpticaExtractAttachmentFrom): IEpticaExctractAttachment[] {
    if (!this.attachmentsRequest$[requestId] || !isArray(this.attachmentsRequest$[requestId])) {
      return [];
    }
    return this.attachmentsRequest$[requestId].filter(a => a.fromType === fromType);
  }

  getRequestDrafts(): Observable<MailRequestResult> {
    return this.epticaService.getDrafts().pipe(
      switchMap(drafts => {
        // if no drafts found
        if (drafts.length <= 0) {
          return of({ total: 0, data: [] });
        }

        const requestIds = drafts.filter(draft => !!draft.requestId).map(draft => draft.requestId!);

        // observable fetching requests from Eptica only if there are "real" eptica requests (i.e. non-MSU drafts). Used for "ask for help" status
        const epticaRequests$ =
          requestIds.length > 0
            ? this.epticaService.search(buildAskForHelpStatusQuery(requestIds))
            : of({ aggregations: {} } as EpticaSearchDto); // else we use an auto-completing observable with placeholder data for the forkJoin to work properly

        return forkJoin(
          // retrieve ask for help status
          epticaRequests$,
          // retrieve detail/customer/account of request id
          ...drafts.map(draft => {
            if (draft.requestId) {
              return this.epticaService.getRequestWithEvents(draft.requestId, {});
            } else {
              // For drafts without eptica request id (outgoing mail draft), we provide a fake request object
              return this.makeFakeRequestForDraft(draft);
            }
          })
        ).pipe(
          switchMap(([agg, ...requests]) =>
            forkJoin(
              requests
                .filter(notNullOrUndefined)
                .filter(r => r.customer && r.customer.id)
                .map(r => r.customer.id)
                .filter((v, i, s) => s.indexOf(v) === i)
                .map(id => this.epticaService.getCustomer(id).pipe(isNotNullOrUndefined(), throwIfEmpty()))
            ).pipe(
              map(customers => ({
                total: 0,
                data: this.toMailRequestList(agg, requests.filter(notNullOrUndefined), customers),
              }))
            )
          ),
          catchError(() => of({ total: 0, data: [] }))
        );
      })
    );
  }

  private buildFrameId(requestId: number): string {
    return `${EPTICA_REQUEST_FRAME_PEFIX}_${requestId}`;
  }

  private buildRequestFrameId(requestId: number): string {
    return `${requestId}`;
  }

  private buildRequestFrameOutgoingId(responseId: string): string {
    return `${responseId}`;
  }

  private buildFrameIdForOutgoing(responseId: string, esCustomerId: string): string {
    return `${EPTICA_OUTGOING_FRAME_PEFIX}_${esCustomerId}_${responseId}`;
  }

  private createEpticaRequest(frameId: string, requestId: number): IEpticaRequestFrame {
    const request = new EpticaRequest(
      frameId,
      requestId,
      this.$log,
      this.$q,
      this.$i18next,
      this.extApp,
      this.toastDeprecatedService,
      this,
      this.epticaService,
      this.sessionDeprecatedService,
      this.historyMailService,
      this.contactFacade,
      this.profileService,
      this.timeUtil,
      this.profileFacade,
      this.contactDeprecatedService,
      this.$timeout
    );
    this.$log.info(`[eptica] Created request with id[${request.getId()}]`);
    return request;
  }

  private createOutgoingPage(frameId: string, responseId: string, idType: CustomerIdType, customerId: string): IEpticaOutgoingFrame {
    const request = new EpticaOutgoingPage(
      frameId,
      idType,
      customerId,
      responseId,
      this.$log,
      this.$q,
      this.$i18next,
      this.extApp,
      this.toastDeprecatedService,
      this,
      this.sessionDeprecatedService,
      this.mailService
    );
    this.$log.info(`[eptica] Created outgoing page with id[${request.getId()}] corresponding to response[${request.getResponseId()}]`);
    return request;
  }

  private getIncomingRequest(requestId: number): Option<IEpticaRequestFrame> {
    const epticaRequestFrameId = this.buildRequestFrameId(requestId);
    return Option.of(this.incomingRequests$[epticaRequestFrameId]);
  }

  private getOutgoingRequest(responseId: string): Option<IEpticaOutgoingFrame> {
    const epticaRequestFrameId = this.buildRequestFrameOutgoingId(responseId);
    return Option.of(this.outgoingRequests$[epticaRequestFrameId]);
  }

  private setIncomingRequest(requestId: number, request: IEpticaRequestFrame): IEpticaRequestFrame {
    const epticaRequestFrameId = this.buildRequestFrameId(requestId);
    return (this.incomingRequests$[epticaRequestFrameId] = request);
  }

  private setOutgoingRequest(responseId: string, request: IEpticaOutgoingFrame): IEpticaOutgoingFrame {
    const epticaRequestFrameId = this.buildRequestFrameOutgoingId(responseId);
    return (this.outgoingRequests$[epticaRequestFrameId] = request);
  }

  private getOrElseEpticaRequest(requestId: number): IEpticaRequestFrame {
    const frameId = this.buildFrameId(requestId);
    // get or else optional
    return this.getIncomingRequest(requestId).getOrElseFunc(() => {
      // initialize new eptica request
      const request: IEpticaRequestFrame = this.createEpticaRequest(frameId, requestId);
      request.preAddRequest();
      // create new observable representing eptica request and save it
      return this.setIncomingRequest(requestId, request);
    });
  }

  private getOrElseEpticaOutgoingRequest(responseId: string, idType: CustomerIdType, customerId: string): IEpticaOutgoingFrame {
    const frameId = this.buildFrameIdForOutgoing(responseId, customerId);
    // get or else optional
    return this.getOutgoingRequest(responseId).getOrElseFunc(() => {
      // initialize new eptica request
      const request: IEpticaOutgoingFrame = this.createOutgoingPage(frameId, responseId, idType, customerId);
      request.preAddRequest();
      // create new observable representing eptica request and save it
      return this.setOutgoingRequest(responseId, request);
    });
  }

  private toMailRequestList(
    search: EpticaSearchDto,
    requests: EpticaExposedRequestForDraft[],
    customers: EpticaExposedCustomer[]
  ): MailRequest[] {
    // Extract data from aggregation
    const { [EpticaFields.ID]: ids } = search.aggregations;

    const aggs = !ids
      ? []
      : ids.buckets.reduce((result, r) => {
          const { key: id, [EpticaFields.EVENT]: events } = r;

          const {
            [AskForHelpEvent.CREATED]: askForHelpCreated,
            [AskForHelpEvent.CREATED_TO_EXTERNAL_SPECIALIST]: askForHelpCreatedToExternalSpecialist,
          } = events;
          const { [AskForHelpStatus.ANSWERED]: answered, [AskForHelpStatus.OPEN]: open } = askForHelpCreated;
          const { [AskForHelpStatus.ANSWERED]: answeredByExternalSpecialist, [AskForHelpStatus.OPEN]: openToExternalSpecialist } =
            askForHelpCreatedToExternalSpecialist;
          // extract first element from buckets responsible
          const extractResponsibleFromBuckets = ({ buckets }: EpticaTreeAggregationDto) => {
            const [bucket] = buckets;
            return Option.of(bucket)
              .map(b => b.key)
              .getOrElse('');
          };
          if (open.doc_count > 0 || openToExternalSpecialist.doc_count > 0) {
            const { [EpticaFields.TARGET_ACCOUNT_ID]: openResponsible } = open;
            result = [
              ...result,
              {
                id: `${id}`,
                status: 'OPEN',
                responsible: extractResponsibleFromBuckets(openResponsible),
              },
            ];
          } else if (answered.doc_count > 0 || answeredByExternalSpecialist.doc_count > 0) {
            const { [EpticaFields.TARGET_ACCOUNT_ID]: answeredResponsible } = answered;
            result = [
              ...result,
              {
                id: `${id}`,
                status: 'ANSWERED',
                responsible: extractResponsibleFromBuckets(answeredResponsible),
              },
            ];
          }
          return result;
        }, [] as { id: string; status: 'OPEN' | 'ANSWERED'; responsible: string }[]);
    // map result
    return requests.map(detail => {
      const id = detail.id;
      const askForHelp = aggs.find(r => r.id === id);
      return {
        id,
        detail,
        customer: detail.customer && customers.find(c => c.id === detail.customer.id),
        askForHelpStatus: {
          status: askForHelp && askForHelp.status,
          responsible: askForHelp && askForHelp.responsible,
        },
      };
    });
  }

  private internalSearchRequests(_data: any): Observable<any> {
    return this.httpDeprecated
      .asObservable()
      .withData(JSON.stringify(_data))
      .withConfig({ headers: { 'Content-Type': 'text/plain; charset=UTF-8' } })
      .post(`${MAIL_FACADE_PREFIX}/searchRequests`)
      .pipe(map(({ data }) => data));
  }

  private internalGetRequests(fn: (user: EpticaUser) => Object): Observable<any> {
    return this.epticaService.user$.pipe(
      first(),
      switchMap(user =>
        this.httpDeprecated
          .asObservable()
          .withData(JSON.stringify(fn(user)))
          .withConfig({ headers: { 'Content-Type': 'text/plain; charset=UTF-8' } })
          .post(`${MAIL_FACADE_PREFIX}/searchRequests`)
          .pipe(map(({ data }) => data))
      )
    );
  }

  private internalGetCounts(fn: (user: EpticaUser) => Object): Observable<{ [key: string]: number }> {
    return this.epticaService.user$.pipe(
      switchMap(user =>
        this.httpDeprecated
          .asObservable()
          .withData(JSON.stringify(fn(user)))
          .withConfig({ headers: { 'Content-Type': 'text/plain; charset=UTF-8' } })
          .post(`${MAIL_FACADE_PREFIX}/getCounters`)
          .pipe(map(({ data }) => data))
      )
    );
  }

  getMatchingRequestsByIds(
    ticketIds: string[],
    sortField: string,
    sortOrder: EASortOrder,
    offset = 0,
    pageSize = 10
  ): Observable<EpticaExposedRequest[]> {
    if (!ticketIds || ticketIds.length === 0) {
      return of([]);
    }
    const request$ = this.epticaService.getRequestList(
      buildMatchingRequestIdsQuery(ticketIds, {
        sort: {
          [sortField]: sortOrder,
        },
        pageSize,
        offset,
      })
    );
    return request$.pipe(
      map(result => result.elements.map(({ id }) => id)),
      switchMap(ids => forkJoin(ids.map(id => this.epticaService.getRequest(id).pipe(isNotNullOrUndefined(), throwIfEmpty()))))
    );
  }

  private decodeEventDescriptionField(request: EpticaExposedRequest): EpticaExposedRequest {
    if (request.events && request.events.length) {
      return {
        ...request,
        events: request.events.map(event => {
          if (event && event.description) {
            const base32Regex = /^[A-Z2-7]+=*$/;
            if (event.description.length % 8 === 0 && base32Regex.exec(event.description)) {
              const descriptionObject = base32Decode(event.description, 'RFC4648');
              const descriptionString = String.fromCharCode.apply(String, new Uint8Array(descriptionObject));

              return {
                ...event,
                description: descriptionString,
              };
            }
          }

          return event;
        }),
      };
    }
    return request;
  }

  getRequests(
    sortField: string,
    sortOrder: EASortOrder,
    type: EpticaRequestType,
    offset = 0,
    pageSize = 10,
    filters: EAFilter[] = []
  ): Observable<MailRequestResult> {
    // first part to get request ids
    return this.internalGetRequests(user => this.buildSearchData(user, { type, sortField, sortOrder, offset, pageSize, filters })).pipe(
      switchMap(response => {
        const ids: string[] = response.ids || [];
        // if no ids found
        if (ids.length <= 0) {
          return of({ total: 0, data: [] });
        }
        // second part to get detail information
        return forkJoin(
          // retrieve ask for help status
          this.epticaService.search(buildAskForHelpStatusQuery(ids)),
          // retrieve detail/customer/account of request id
          ...ids.map(id => this.epticaService.getRequestWithEvents(id, { eventTypes: EVENT_TYPES }))
        ).pipe(
          switchMap(([agg, ...requests]) =>
            forkJoin(
              requests
                .filter(notNullOrUndefined)
                .filter(r => r.customer)
                .map(r => r.customer.id)
                .filter((v, i, s) => s.indexOf(v) === i)
                .map(id => this.epticaService.getCustomer(id).pipe(isNotNullOrUndefined(), throwIfEmpty()))
            ).pipe(
              map(customers => ({
                total: response.total,
                data: this.toMailRequestList(
                  agg,
                  requests.filter(notNullOrUndefined).map(r => this.decodeEventDescriptionField(r)),
                  customers
                ),
              }))
            )
          ),
          catchError(() => of({ total: 0, data: [] }))
        );
      })
    );
  }

  getRequestsOfCsrs(
    searchDtos: SearchDto[],
    sortField: string,
    sortOrder: SortOrder,
    type: EpticaRequestType,
    offset = 0,
    pageSize = 10,
    filters: EAFilter[] = []
  ): Observable<MailRequestResult> {
    // first part to get request ids
    return this.internalSearchRequests(
      this.buildSearchDataOfCsrs(searchDtos, { type, sortField, sortOrder, offset, pageSize, filters })
    ).pipe(
      switchMap(response => {
        const ids: string[] = response.ids || [];
        // if no ids found
        if (ids.length <= 0) {
          return of({ total: 0, data: [] });
        }
        // second part to get detail information
        return forkJoin(
          // retrieve ask for help status
          this.epticaService.search(buildAskForHelpStatusQuery(ids)),
          // retrieve detail/customer/account of request id
          ...ids.map(id => this.epticaService.getRequestWithEvents(id, { eventTypes: EVENT_TYPES }))
        ).pipe(
          switchMap(([agg, ...requests]) =>
            forkJoin(
              requests
                .filter(notNullOrUndefined)
                .filter(r => r.customer)
                .map(r => r.customer.id)
                .filter((v, i, s) => s.indexOf(v) === i)
                .map(id => this.epticaService.getCustomer(id).pipe(isNotNullOrUndefined(), throwIfEmpty()))
            ).pipe(
              map(customers => ({
                total: response.total,
                data: this.toMailRequestList(agg, requests.filter(notNullOrUndefined), customers),
              }))
            )
          ),
          catchError(() => of({ total: 0, data: [] }))
        );
      })
    );
  }

  isRequestWithOpenedAskForHelp(requestId: string): Observable<boolean> {
    return forkJoin([this.epticaService.search(buildAskForHelpStatusQuery([requestId])), this.epticaService.getRequest(requestId)]).pipe(
      switchMap(([agg, req]) => {
        const evaluateAskForHelpStatus = (agg, req, customer) => {
          const mailRequests = customer ? this.toMailRequestList(agg, [req], [customer]) : this.toMailRequestList(agg, [req], []);
          if (mailRequests.length === 1) {
            const { askForHelpStatus } = mailRequests[0];
            return askForHelpStatus.status === 'OPEN';
          }
          return false;
        };
        if (req) {
          return req.customer && req.customer.id
            ? this.epticaService.getCustomer(req.customer!.id).pipe(map(customer => evaluateAskForHelpStatus(agg, req, customer)))
            : of(evaluateAskForHelpStatus(agg, req, undefined));
        } else {
          return of(false);
        }
      })
    );
  }

  private buildSearchDataOfCsrs(
    searchDtos: SearchDto[],
    payload: {
      type: EpticaRequestType;
      sortField?: string;
      sortOrder?: string;
      offset?: number;
      pageSize?: number;
      filters?: EAFilter[];
    }
  ): any {
    let csrUuid = '';
    let accountId = '';
    let accountGroupPath = '';
    if (searchDtos.length === 1) {
      csrUuid = searchDtos[0].csrUuid;
      accountId = searchDtos[0].accountId;
      accountGroupPath = searchDtos[0].accountGroupPath;
    }
    return {
      accountId,
      accountGroupPath,
      type: payload.type,
      sortField: payload.sortField || '',
      sortOrder: payload.sortOrder || '',
      pageSize: payload.pageSize || 0,
      offset: payload.offset || 0,
      filters: (payload.filters || []).map(f => toJson(f)),
      csrUuid,
      searchDtos,
    };
  }

  getSearchRequestCount(types: EpticaRequestType[]): Observable<{ [key: string]: number }> {
    return this.internalGetCounts(user => this.buildCountData(user, { types })).pipe(
      map(counters => {
        if (!this.sessionDeprecatedService.isSupFeatureEnabled) {
          return counters;
        }
        const { [EpticaRequestType.RELATED]: related, [EpticaRequestType.RELATED_FOR_SUP]: relatedForSup, ...rest } = counters;
        return {
          ...rest,
          [EpticaRequestType.RELATED]: relatedForSup ?? related ?? 0,
        };
      })
    );
  }

  private buildSearchData(
    user: EpticaUser,
    payload: {
      type: EpticaRequestType;
      sortField?: string;
      sortOrder?: string;
      offset?: number;
      pageSize?: number;
      filters?: EAFilter[];
    }
  ): any {
    return {
      accountId: user.accountId,
      accountGroupPath: user.accountGroupPath,
      type: this.overrideTypeRelatedForSup([payload.type])[0],
      sortField: payload.sortField || '',
      sortOrder: payload.sortOrder || '',
      pageSize: payload.pageSize || 0,
      offset: payload.offset || 0,
      filters: (payload.filters || []).map(f => toJson(f)),
      activeSkills: this.agentConfigDeprecatedService
        .channelActiveSkills(ChannelUtil.toString(ChannelUtil.INCOMING_MAIL))
        .map(s => s.skill.id),
      accessiblePerimeters: this.getVisiblePerimeter(),
      csrUuid: this.sessionDeprecatedService.getProfile().id,
    };
  }

  private buildCountData(
    user: EpticaUser,
    payload: {
      types: EpticaRequestType[];
    }
  ): any {
    return {
      accountId: user.accountId,
      accountGroupPath: user.accountGroupPath,
      types: this.overrideTypeRelatedForSup(payload.types),
      activeSkills: this.agentConfigDeprecatedService
        .channelActiveSkills(ChannelUtil.toString(ChannelUtil.INCOMING_MAIL))
        .map(s => s.skill.id),
      accessiblePerimeters: this.getVisiblePerimeter(),
      csrUuid: this.sessionDeprecatedService.getProfile().id,
    };
  }

  private overrideTypeRelatedForSup(types: EpticaRequestType[]): EpticaRequestType[] {
    if (!this.sessionDeprecatedService.isSupFeatureEnabled) {
      return types;
    }
    if (!types.some(type => type === EpticaRequestType.RELATED)) {
      return types;
    }
    return [...types.filter(type => type !== EpticaRequestType.RELATED), EpticaRequestType.RELATED_FOR_SUP];
  }

  getRequestVisibility(requestIds: number[]): IPromise<{ [id: string]: boolean }> {
    const data = toJson({
      requestIds: requestIds,
      accessiblePerimeters: this.getVisiblePerimeter(),
    });
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
        },
      })
      .withData(data)
      .post(`${MAIL_FACADE_PREFIX}/getRequestVisibilityForUser`)
      .then(success => success.data);
  }

  private getVisiblePerimeter(): string[] {
    const profile = this.sessionDeprecatedService.getProfile();
    const visibleTeams = profile.visibleTeams ?? [];
    const accessibleDomainUuids = profile.accessibleDomainUuids ?? [];
    const teamId = profile.teamId;

    if (this.sessionDeprecatedService.isSupFeatureEnabled) {
      return [...visibleTeams, ...accessibleDomainUuids];
    }
    return teamId ? [...accessibleDomainUuids, teamId] : accessibleDomainUuids;
  }

  closeOutgoingForm(): IPromise<void> {
    return this.httpDeprecated
      .asPromise()
      .get(`${CSR_FACADE_PREFIX}/closeUnitMail`)
      .catch(() => {
        this.$log.error(`[eptica] Failed to close an outgoing mail`);
      });
  }

  getCsrIasId(csrEsId: number): IPromise<string> {
    const deferred = this.$q.defer<string>();
    this.getEpticaInfo().then(
      info =>
        this.$http.get(`${info.iasUrl}/api/account/${csrEsId}`).then(
          response => {
            const iasId: string = (response.data as any).iasId;
            if (isString(iasId)) {
              return deferred.resolve(iasId);
            }
            deferred.reject(`epticaService.getCsrIasId(${csrEsId}) => invalid iasId`);
          },
          error => {
            deferred.reject(`epticaService.getCsrIasId(${csrEsId}) => Error while retrieving csr iasId : ${error}`);
          }
        ),
      error => {
        deferred.reject(
          `epticaService.getCsrIasId(${csrEsId}) => Error while retrieving csr iasId (connection info not present) : ${error}`
        );
      }
    );
    return deferred.promise;
  }

  getCsrIasIdObs(csrEsId: number): Observable<string> {
    return RxUtil.fromPromise(() => this.getCsrIasId(csrEsId));
  }

  getCsrEsId(uuid: string): IPromise<number> {
    return RxUtil.toPromise(this.getCsrEsIdObs(uuid), this.$q);
  }

  getCsrEsIdObs(uuid: string): Observable<number> {
    return this.httpDeprecated
      .asObservable()
      .get(`${CSR_FACADE_PREFIX}/getMailUserInfoOfCsr`, [uuid])
      .pipe(map(({ data }) => data.epticaEsId));
  }

  searchLastMailRequestSend(accountId: number, retries = 6): IPromise<number> {
    const query = buildSearchLastSentMessageFrom(accountId);
    return this.getEpticaInfo().then(info =>
      this.$http
        .post(`${info.iasUrl}/api/request/list/search/as/account/${accountId}`, query)
        .then(response => {
          const data: any = response != null ? response.data : {};
          try {
            if (data && data.elements && data.elements.length > 0) {
              return parseInt(data.elements[0].id, 10);
            } else {
              if (retries > 0) {
                return EpticaHelper.delay(this.$q, this.$timeout, 3000).then(() => this.searchLastMailRequestSend(accountId, retries - 1));
              }
              this.$log.error('The search WS has no response while searching contacts !');
              return this.$q.reject('The search WS has no response while searching contacts !');
            }
          } catch (e) {
            this.$log.error(`Error while searching the contacts : ${e}`);
            return this.$q.reject(`Error while searching the contacts : ${e}`);
          }
        })
        .catch(e => {
          this.$log.error(`Error while retrieving the contacts: ${e}`);
          return this.$q.reject(`Error while retrieving the contacts: ${e}`);
        })
    );
  }

  updateRequestFields(requestId: RequestId, fields: {}): IPromise<void> {
    const defer = this.$q.defer<void>();
    this.httpDeprecated
      .asPromise()
      .withData(fields)
      .withConfig({ headers: { 'Content-Type': 'text/plain; charset=UTF-8' } })
      .post(`${MAIL_FACADE_PREFIX}/updateRequestFields`, [requestId])
      .then(
        success => (success && success.data !== 'false' ? defer.resolve() : defer.reject()),
        error => {
          this.$log.error(`Error while updating eptica request {} fields : {}`, requestId, error);
          defer.reject();
        }
      );
    return defer.promise;
  }

  transferRequestToSkill(
    requestId: RequestId,
    agent: string,
    targetSkills: Skill[],
    sourceSkills: Skill[],
    comment: string
  ): IPromise<RequestId> {
    return RxUtil.toPromise(
      this.mailService
        .forward(requestId, {
          type: 'SKILL',
          sourceSkillUuids: sourceSkills.map(skill => skill.id),
          targetSkillUuids: targetSkills.map(skill => skill.id),
          bounce: {
            type: 'FORWARD_BY_SKILL',
            agent,
            comment,
            source: sourceSkills.map(skill => ({ uuid: skill.id, name: skill.name })),
            target: targetSkills.map(skill => ({ uuid: skill.id, name: skill.name })),
          },
        })
        .pipe(map(() => requestId)),
      this.$q
    );
  }

  transferRequestToTeam(
    requestId: RequestId,
    agent: string,
    targetTeam: Entity,
    comment: string,
    sourceSkills: Skill[],
    targetSkills: Skill[]
  ): IPromise<RequestId> {
    return RxUtil.toPromise(
      this.mailService
        .forward(requestId, {
          type: 'TEAM',
          targetTeamUuid: targetTeam.id,
          sourceSkillUuids: sourceSkills.map(skill => skill.id),
          targetSkillUuids: targetSkills.map(skill => skill.id),
          bounce: {
            type: 'FORWARD_BY_TEAM',
            agent,
            comment,
            extraData: {
              target: targetTeam.name,
            },
          },
        })
        .pipe(map(() => requestId)),
      this.$q
    );
  }

  transferRequestToAgent(
    requestId: RequestId,
    target: any,
    comment: string,
    iasId: string,
    request?: IEpticaRequestFrame
  ): Observable<RequestId> {
    // retrieve custormer es id
    return this.transferRequest(
      requestId,
      {
        type: 'AGENT',
        targetAgentUuid: target.uuid,
      },
      r => r.fillTransfertToAgent(iasId, comment),
      request
    );
  }

  safeTransferRequestToAgent(
    requestId: RequestId,
    target: any,
    comment: string,
    iasId: string,
    request?: IEpticaRequestFrame
  ): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.transferRequestToAgent(requestId, target, comment, iasId, request));
  }

  transferRequestToEmail(requestId: RequestId, email: string, comment: string, request?: IEpticaRequestFrame): Observable<RequestId> {
    // execute transfer with fill form email
    return this.transferRequest(
      requestId,
      {
        type: 'EMAIL',
        targetEmail: email,
      },
      r => r.fillTransferToEmail(email, comment),
      request
    );
  }

  safeTransferRequestToEmail(requestId: RequestId, email: string, comment: string, request?: IEpticaRequestFrame): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.transferRequestToEmail(requestId, email, comment, request));
  }

  private transferRequest(
    requestId: RequestId,
    data: MailForwardParam,
    fillForm: (r: IEpticaRequestFrame) => void,
    request?: IEpticaRequestFrame
  ): Observable<RequestId> {
    // if we have the frame, do not reload it in order to keep the PJ from the existing frame
    const getFrame$: Observable<IEpticaRequestFrame> = !!request
      ? from([request])
      : this.loadRequest$({ requestId: +requestId, forceReload: true });

    return this.mailService.forward(requestId, data).pipe(
      // load frame
      // but without reloading the frame in order to keep the PJ from the existing frame
      switchMap(() =>
        getFrame$.pipe(
          // execute fill form depend of type delegate
          tap(frame => {
            frame.selectAllMessageRequestHistory();
            frame.toggleDelegatePanel(true);
            fillForm(frame);
          }),
          // call delegate method from frame and wait responde
          switchMap(frame =>
            RxUtil.fromPromise(() => frame.delegate()).pipe(
              // if json return error we throw error to retry operator
              tap(json => {
                if (json.messageState === 'error') {
                  throw '';
                }
              }),
              // retry 3 time each 1s
              retryWhen(err => err.pipe(delay(1000), take(3))),
              // finish frame and return id
              tap(() => frame.finish())
            )
          )
        )
      ),
      map(() => requestId)
    );
  }

  forwardRequestToSkill(
    requestId: RequestId,
    data: {
      source: (Skill | string)[];
      target: (Skill | string)[];
      comment: string;
      attachments: File[];
    }
  ): Observable<RequestId> {
    return this.mailService
      .forwardToSkill(
        requestId,
        {
          target: data.target.map(skillOrId => (isString(skillOrId) ? skillOrId : skillOrId.id)),
          comment: data.comment,
        },
        data.attachments
      )
      .pipe(map(() => requestId));
  }

  safeForwardRequestToSkill(
    requestId: RequestId,
    data: {
      source: (Skill | string)[];
      target: (Skill | string)[];
      comment: string;
      attachments: File[];
    }
  ): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.forwardRequestToSkill(requestId, data));
  }

  forwardRequestToTeam(
    requestId: RequestId,
    data: {
      target: Entity | string;
      sourceSkills: (Skill | string)[];
      targetSkills: (Skill | string)[];
      comment: string;
      attachments: File[];
    }
  ): Observable<RequestId> {
    return this.mailService
      .forwardToTeam(
        requestId,
        {
          target: isString(data.target) ? data.target : data.target.id,
          targetSkillIds: data.targetSkills.map(skillOrId => (isString(skillOrId) ? skillOrId : skillOrId.id)),
          comment: data.comment,
        },
        data.attachments
      )
      .pipe(map(() => requestId));
  }

  safeForwardRequestToTeam(
    requestId: RequestId,
    data: {
      target: Entity | string;
      sourceSkills: (Skill | string)[];
      targetSkills: (Skill | string)[];
      comment: string;
      attachments: File[];
    }
  ): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.forwardRequestToTeam(requestId, data));
  }

  forwardRequestToAgent(
    requestId: RequestId,
    data: {
      target: string;
      comment: string;
      attachments: File[];
      includedMessages: string[];
    }
  ): Observable<RequestId> {
    return this.mailService
      .forwardToAgent(
        requestId,
        {
          target: data.target,
          comment: data.comment,
          includedMessages: data.includedMessages,
        },
        data.attachments
      )
      .pipe(map(() => requestId));
  }

  safeForwardRequestToAgent(
    requestId: RequestId,
    data: {
      target: string;
      comment: string;
      attachments: File[];
      includedMessages: string[];
    }
  ): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.forwardRequestToAgent(requestId, data));
  }

  forwardRequestToMail(
    requestId: RequestId,
    data: {
      target: string;
      comment: string;
      attachments: File[];
      includedMessages: string[];
    }
  ): Observable<RequestId> {
    return this.mailService
      .forwardToMail(
        requestId,
        {
          target: data.target,
          comment: data.comment,
          includedMessages: data.includedMessages,
        },
        data.attachments
      )
      .pipe(map(() => requestId));
  }

  safeForwardRequestToMail(
    requestId: RequestId,
    data: {
      target: string;
      comment: string;
      attachments: File[];
      includedMessages: string[];
    }
  ): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.forwardRequestToMail(requestId, data));
  }

  linkRequest(requestId: RequestId, contactConnectionId: string): Observable<RequestId> {
    return this.mailService.link(requestId, { contactConnectionId }).pipe(map(() => requestId));
  }

  safeLinkRequest(requestId: RequestId, contactConnectionId: string): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.linkRequest(requestId, contactConnectionId));
  }

  assignRequest(requestId: RequestId): Observable<RequestId> {
    return this.mailService.assign(requestId, {}).pipe(map(() => requestId));
  }

  safeAssignRequest(requestId: RequestId): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.assignRequest(requestId));
  }

  closeRequest(requestId: RequestId, comment = ''): Observable<RequestId> {
    return this.mailService.close(requestId, { comment }).pipe(map(() => requestId));
  }

  safeCloseRequest(requestId: RequestId, comment?: string): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.closeRequest(requestId, comment));
  }

  assistRequest(requestId: RequestId): IPromise<void> {
    return RxUtil.toPromise(this.mailService.askForHelpToMail(requestId, 'frameMode'), this.$q);
  }

  askForHelpAnswer(requestId: RequestId): IPromise<void> {
    return RxUtil.toPromise(this.mailService.answerAskForHelp(requestId, 'frameMode'), this.$q);
  }

  bounceRequest(requestId: RequestId, comment: IMailComment, responseId = ''): IPromise<RequestId> {
    return RxUtil.toPromise(this.giveUpRequest(requestId, comment, responseId), this.$q);
  }

  safeBounceRequest(requestId: RequestId, comment: IMailComment, responseId = ''): IPromise<RequestId> {
    return this.checkProcessing(requestId, () => this.bounceRequest(requestId, comment, responseId));
  }

  giveUpRequest(requestId: RequestId, comment: IMailComment, responseId = '', asAccountUuid = ''): Observable<RequestId> {
    return this.mailService
      .bounce(requestId, {
        type: 'BOUNCE',
        comment: comment.comment || '',
        responseId: responseId,
        asAccountUuid: asAccountUuid,
      })
      .pipe(map(() => requestId));
  }

  safeGiveUpRequest(requestId: RequestId, comment: IMailComment, responseId = ''): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.giveUpRequest(requestId, comment, responseId));
  }

  deferRequest(requestId: RequestId, memo?: Memo, bookmark?: boolean): Observable<RequestId> {
    return this.mailService
      .defer(requestId, {
        data: {
          comment: (memo && memo.message) || '',
          bookmark: bookmark || false,
        },
      })
      .pipe(map(() => requestId));
  }

  safeDeferRequest(requestId: RequestId, memo?: Memo, bookmark?: boolean): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.deferRequest(requestId, memo, bookmark));
  }

  askApprovalRequest(requestId: RequestId): Observable<RequestId> {
    return this.mailService.askForApproval(requestId, 'frameMode').pipe(map(() => requestId));
  }

  safeAskApprovalRequest(requestId: RequestId): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.askApprovalRequest(requestId));
  }

  acceptApprovalRequest(requestId: RequestId): Observable<RequestId> {
    return this.mailService.acceptAskForApproval(requestId, 'frameMode').pipe(map(() => requestId));
  }

  safeAcceptApprovalRequest(requestId: RequestId): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.acceptApprovalRequest(requestId));
  }

  rejectApprovalRequest(requestId: RequestId): Observable<RequestId> {
    return this.mailService.rejectAskForApproval(requestId, 'frameMode').pipe(map(() => requestId));
  }

  safeRejectApprovalRequest(requestId: RequestId): Observable<RequestId> {
    return this.checkProcessing$(requestId, this.rejectApprovalRequest(requestId));
  }

  checkProcessing$<T>(requestId: RequestId, action: Observable<T> = of<T>({} as T)): Observable<T> {
    return this.mailService.getBookingInfo(requestId).pipe(
      switchMap(info => {
        if (info) {
          this.toasterFacade.notification({
            message: this.translateService.instant('agent:toast.mail_popin_action_failed', {
              subject: truncate(info.requestSubject),
              firstname: info.agentFirstname,
              lastname: info.agentLastname,
            }),
            duration: 5000,
          });
          return EMPTY;
        }
        return action;
      })
    );
  }

  checkProcessing<T>(requestId: RequestId, action: () => IPromise<T> = () => this.$q.resolve({} as T)): IPromise<T> {
    return RxUtil.toPromiseEmited(this.checkProcessing$(requestId, RxUtil.fromPromise(action)), this.$q);
  }

  /**
   * Create the contacts for the CC and BCC fields
   * (the contacts will be create only in there is no existing contact with that adress)
   *
   * @param adresses The emails adresses
   */
  public createContactsFromEmails(adresses: Array<string>): Observable<void> {
    return this.httpDeprecated.asObservable().withData(adresses).post(`${CSR_FACADE_PREFIX}/createContactsFromEmails`);
  }

  removePJEventsForLoader(): void {
    // remove upload events
    const bodyElem = window.top?.document.querySelector('body');
    if (!!bodyElem) {
      bodyElem.removeEventListener('addedfile_for_contact', this.addingListener);
      bodyElem.removeEventListener('sending_file_for_contact', this.sendingListener);
      bodyElem.removeEventListener('upload_queue_complete_for_contact', this.completeListener);
      this.$timeout.cancel();
    }
  }

  private activeLoader() {
    if (document.getElementById(AREA_MAIN_ACTION_ZONE)) {
      this.loaderService.active(AREA_MAIN_ACTION_ZONE);
      // do not block buttons after x seconds (in case of the dropzone error event is never sent)
      this.$timeout(MAX_TIMEOUT_FOR_LOADER).then(() => {
        this.loaderService.dispose(AREA_MAIN_ACTION_ZONE);
        this.$log.info('timeout active loader');
      });
    }
  }

  private readonly addingListener = () => {
    this.activeLoader();
  };
  private readonly sendingListener = () => {
    this.activeLoader();
  };
  private readonly completeListener = () => {
    this.loaderService.dispose(AREA_MAIN_ACTION_ZONE);
    this.$timeout.cancel();
  };

  addPJEventsForLoader(): void {
    // receipt of upload events
    const bodyElem = window.top?.document.querySelector('body');
    if (!!bodyElem) {
      bodyElem.addEventListener('addedfile_for_contact', this.addingListener);
      bodyElem.addEventListener('sending_file_for_contact', this.sendingListener);
      bodyElem.addEventListener('upload_queue_complete_for_contact', this.completeListener);
    }
  }

  makeFakeRequestForDraft(draftDto: EpticaExposedDraft): Observable<EpticaExposedRequestForDraft> {
    if (!!draftDto.customerId) {
      return this.epticaService.getCustomer(draftDto.customerId).pipe(
        switchMap(customer =>
          of({
            id: '',
            uri: '',
            iasId: '',
            creationDate: Number(new Date()),
            customer: {
              iasId: customer && customer.iasId ? customer.iasId : '',
              id: customer && customer.id ? customer.id : '',
              uri: customer && customer.uri ? customer.uri : '',
            },
            ticketNumber: -1,
            locale: '',
            requestType: EpticaType.email,
            requestState: EpticaRequestState.postponed,
            forwarded: false,
            hasAttachment: draftDto.message.attachments ? draftDto.message.attachments.length > 0 : false,
            subject: draftDto.message.subject ? draftDto.message.subject : '',
            text: draftDto.message.html ? draftDto.message.html : '',
            accountResponsible: undefined,
            accountGroupResponsible: undefined,
            fields: [],
            incomingChannel: {
              id: '',
              uri: '',
              iasId: '',
              name: '',
            },
            events: undefined,
            draftUuid: draftDto.id,
            requestRead: false,
            hasDraft: true,
            lastReadPivotEventId: '',
          })
        )
      );
    } else {
      return of({
        id: '',
        uri: '',
        iasId: '',
        creationDate: undefined,
        customer: {
          iasId: '',
          id: draftDto.customerId,
          uri: '',
        },
        ticketNumber: -1,
        locale: '',
        requestType: EpticaType.email,
        requestState: EpticaRequestState.postponed,
        forwarded: false,
        hasAttachment: draftDto.message.attachments ? draftDto.message.attachments.length > 0 : false,
        subject: draftDto.message.subject ? draftDto.message.subject : '',
        text: draftDto.message.html ? draftDto.message.html : '',
        accountResponsible: undefined,
        accountGroupResponsible: undefined,
        fields: [],
        incomingChannel: {
          id: '',
          uri: '',
          iasId: '',
          name: '',
        },
        events: undefined,
        draftUuid: draftDto.id,
        requestRead: false,
        hasDraft: true,
        lastReadPivotEventId: '',
      });
    }
  }
}

export default 'epticaDeprecatedService';
